import React, { useEffect, useState } from 'react';

export default function SampleConceptViewer({ isOpen, onClose }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [isProtected, setIsProtected] = useState(true);
  
  // Your sample images array
  const sampleImages = [
    {
      url: '/samples/concept-1-alterations.jpg',
      title: 'Street Frontage - Artist\'s Impression',
      description: 'Alterations + Additions - 2-Storey Side Extension'
    },
    {
      url: '/samples/concept-2-site-plan.jpg', 
      title: 'Proposed Site/Roof Plan',
      description: 'Concept Design - Site Layout'
    },
    {
      url: '/samples/concept-3-floor-plan.jpg',
      title: 'Proposed First Floor Plan', 
      description: 'New Master Suite + Ensuite'
    },
    {
      url: '/samples/concept-4-ground-plan.jpg',
      title: 'Proposed Ground Floor Plan',
      description: 'New Double Garage + Modified Driveway'
    },
    {
      url: '/samples/concept-5-street-view.jpg',
      title: 'Street Frontage - Artist\'s Impression',
      description: 'Concept Design Visualization'
    },
    {
      url: '/samples/concept-6-rear-view.jpg',
      title: 'View From Rear Garden',
      description: 'Artist\'s Impression - Rear Elevation'
    }
  ];

  const goToPrevious = () => {
    setCurrentImage(prev => (prev > 0 ? prev - 1 : sampleImages.length - 1));
  };

  const goToNext = () => {
    setCurrentImage(prev => (prev < sampleImages.length - 1 ? prev + 1 : 0));
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Protection implementation
  useEffect(() => {
    if (!isOpen || !isProtected) return;

    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e) => {
      // Block Ctrl+P (print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+S (save)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Block F12 (dev tools)
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
    };

    // Apply listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // CSS injection
    const style = document.createElement('style');
    style.innerHTML = `
      .sample-viewer * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      
      @media print {
        .sample-viewer {
          display: none !important;
        }
      }
      
      .sample-viewer img {
        pointer-events: none !important;
        -webkit-user-drag: none !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
      style.remove();
    };
  }, [isOpen, isProtected]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center sample-viewer">
      <div className="bg-white rounded-lg max-w-5xl max-h-[90vh] w-full mx-4 overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-blue-700 text-white p-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Sample Concept Design Package</h3>
            <button onClick={onClose} className="text-2xl hover:text-gray-200">×</button>
          </div>
        </div>

        {/* Image Display with Arrow Navigation */}
        <div className="relative bg-gray-100 flex-grow flex items-center justify-center" style={{ height: '60vh' }}>
          
          {/* Left Arrow */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 z-10 bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Image */}
          <div className="h-full flex items-center justify-center p-4">
            <img
              src={sampleImages[currentImage].url}
              alt={sampleImages[currentImage].title}
              className="max-h-full max-w-full object-contain shadow-lg"
              draggable={false}
            />
          </div>

          {/* Right Arrow */}
          <button
            onClick={goToNext}
            className="absolute right-4 z-10 bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Image Counter and Info */}
        <div className="bg-white p-3 border-t flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-800">{sampleImages[currentImage].title}</p>
              <p className="text-xs text-gray-500">{sampleImages[currentImage].description}</p>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              {currentImage + 1} of {sampleImages.length}
            </div>
          </div>
        </div>

        {/* Footer Warning */}
        <div className="bg-yellow-50 p-3 border-t flex-shrink-0">
          <p className="text-xs text-gray-700">
            ⚠️ These samples are for demonstration only. Full documents provided after payment.
          </p>
        </div>
      </div>
    </div>
  );
}